import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text, Skeleton } from '../../../../components';
import { PULSE_SEGMENT_COLORS } from '../../../../theme/tokens';
import { useMountAnimation } from '../../../../hooks/useMountAnimation';
import type { PulseSegment, PulseReachStat } from '../../../../mock-data/types';
import { SectionCard } from '../SectionCard';
import { PulseBadge } from '../PulseBadge';
import {
  Allocation,
  SubLabel,
  SegBar,
  Seg,
  Legend,
  LegendItem,
  Dot,
  ReachStats,
  ReachStat,
  RsLabel,
  RsValue,
  RsNote,
  NotAvailable,
} from './YourAsset.styles';

export type YourAssetProps = {
  players: string;
  segments: PulseSegment[];
  /** No value-tier source: render the share bar's "not available yet" placeholder. */
  segmentsMissing?: boolean;
  reach: PulseReachStat[];
  /** Reveal in-progress content (the value-tier bar + no-backing-field reach stats);
   *  when false they are omitted entirely. */
  showInProgress?: boolean;
  /** While true, the share bar + reach figures show placeholders (the endpoint is loading). */
  loading?: boolean;
};

// Reach-stat names are a fixed UI vocabulary, translated keyed off their stable
// English source (shared by the live body and the skeleton).
const useReachLabels = (): Record<string, string> => {
  const { t } = useTranslation();
  return {
    '30-day Retention': t('30-day Retention'),
    'Revenue per player': t('Revenue per player'),
    'VIP Contribution': t('VIP Contribution'),
  };
};

/** One reach stat's value cell — `?` for a figure with no backing field, the value
 *  plus an optional trend badge otherwise. */
const ReachValue = ({ r }: { r: PulseReachStat }): React.ReactElement => {
  if (r.missing) return <RsValue>?</RsValue>;
  return (
    <RsValue>
      {r.value}{' '}
      {r.delta ? (
        <React.Fragment>
          <PulseBadge text={r.delta} down={r.down} /> <RsNote text="vs last" tag="span" />
        </React.Fragment>
      ) : null}
    </RsValue>
  );
};

/** The "Players by value" allocation — the share bar + legend, or a muted "not
 *  available yet" placeholder when there is no value-tier source. The bar's
 *  segments grow in on mount. */
const AssetAllocation = ({ segments, segmentsMissing }: { segments: PulseSegment[]; segmentsMissing: boolean }): React.ReactElement => {
  const { t } = useTranslation();
  const mounted = useMountAnimation();

  const SEGMENT_LABELS: Record<string, string> = {
    VIP: t('VIP'),
    Regular: t('Regular'),
    Casual: t('Casual'),
    New: t('New'),
  };

  return (
    <Allocation>
      <SubLabel text="Players by value" tag="span" />
      {segmentsMissing ? (
        <NotAvailable text="Player value tiers aren't available yet" tag="span" />
      ) : (
        <React.Fragment>
          <SegBar>
            {segments.map((s) => (
              <Seg key={s.id} $color={PULSE_SEGMENT_COLORS[s.id]} $width={mounted ? s.pct : 0} />
            ))}
          </SegBar>
          <Legend>
            {segments.map((s) => (
              <LegendItem key={s.id}>
                <Dot $color={PULSE_SEGMENT_COLORS[s.id]} />
                <Text text={SEGMENT_LABELS[s.label] ?? s.label} tag="span" /> {s.pct}%
              </LegendItem>
            ))}
          </Legend>
        </React.Fragment>
      )}
    </Allocation>
  );
};

/**
 * The live body — the allocation grows in on mount, so it is mounted only once the
 * data is in (gated below) and owns its own `useMountAnimation`, letting the grow-in
 * replay on reveal rather than running unseen behind a skeleton.
 */
const AssetBody = ({
  segments,
  segmentsMissing,
  showAllocation,
  reach,
}: {
  segments: PulseSegment[];
  segmentsMissing: boolean;
  showAllocation: boolean;
  reach: PulseReachStat[];
}): React.ReactElement => {
  const reachLabels = useReachLabels();
  return (
    <React.Fragment>
      {showAllocation ? <AssetAllocation segments={segments} segmentsMissing={segmentsMissing} /> : null}
      <ReachStats $count={reach.length}>
        {reach.map((r) => (
          <ReachStat key={r.label}>
            <RsLabel text={reachLabels[r.label] ?? r.label} tag="span" />
            <ReachValue r={r} />
          </ReachStat>
        ))}
      </ReachStats>
    </React.Fragment>
  );
};

/** The loading stand-in: the allocation placeholder (or its own "not available"
 *  state) plus reach rows whose figures are placeholders — except `missing` stats,
 *  which show `?` immediately (no value is coming). Labels stay (static chrome). */
const AssetSkeleton = ({
  segments,
  segmentsMissing,
  showAllocation,
  reach,
}: {
  segments: PulseSegment[];
  segmentsMissing: boolean;
  showAllocation: boolean;
  reach: PulseReachStat[];
}): React.ReactElement => {
  const reachLabels = useReachLabels();
  return (
    <React.Fragment>
      {showAllocation ? (
        <Allocation>
          <SubLabel text="Players by value" tag="span" />
          {segmentsMissing ? <NotAvailable text="Player value tiers aren't available yet" tag="span" /> : <Skeleton block height={8} radius={4} />}
          {segmentsMissing ? null : (
            <Legend>
              {segments.map((s) => (
                <Skeleton key={s.id} width={84} height={12} radius={6} />
              ))}
            </Legend>
          )}
        </Allocation>
      ) : null}

      <ReachStats $count={reach.length}>
        {reach.map((r) => (
          <ReachStat key={r.label}>
            <RsLabel text={reachLabels[r.label] ?? r.label} tag="span" />
            {r.missing ? (
              <RsValue>?</RsValue>
            ) : (
              <RsValue>
                <Skeleton width="3ch" height="0.72em" radius={6} />
              </RsValue>
            )}
          </ReachStat>
        ))}
      </ReachStats>
    </React.Fragment>
  );
};

/**
 * "Your Asset" — the player base broken down by value (a stacked share bar with a
 * legend, segments growing in on mount) and three reach stats below. Figures with no
 * backing field render `?` (player value tiers, 30-day retention).
 */
export const YourAsset = ({ players, segments, segmentsMissing = false, reach, showInProgress = false, loading = false }: YourAssetProps): React.ReactElement => {
  const { t } = useTranslation();

  // Hide the in-progress value-tier bar and the no-backing-field reach stats unless the
  // flag reveals them.
  const showAllocation = !segmentsMissing || showInProgress;
  const visibleReach = showInProgress ? reach : reach.filter((r) => !r.missing);

  return (
    // Drop the "• N Players" suffix while the roster pages in (players === ''), so no
    // misleading placeholder count flashes; it appears once the count (or `?`) resolves.
    <SectionCard title={t('Your Asset')} suffix={players ? t('• {{count}} Players') : undefined} suffixPlaceholders={{ count: players }}>
      {loading ? (
        <AssetSkeleton segments={segments} segmentsMissing={segmentsMissing} showAllocation={showAllocation} reach={visibleReach} />
      ) : (
        <AssetBody segments={segments} segmentsMissing={segmentsMissing} showAllocation={showAllocation} reach={visibleReach} />
      )}
    </SectionCard>
  );
};

export default YourAsset;
